import SizeIcon from "../../../assets/icons/SizeIcon";
import BuildingIcon from "../../../assets/icons/BuildingIcon";
import PlanIcon from "../../../assets/icons/PlanIcon";
import PenIcon from "../../../assets/icons/PenIcon";
import LocationIcon from "../../../assets/icons/LocationIcon";
import Modal from "../../../components/modal/Modal";
import { useState } from "react";
import { FaRobot } from "react-icons/fa";
import RequestPlanForm from "../../../components/forms/requestPlanForm/RequestPlanForm";

const sortItems = [
  {
    id: 1,
    key: "last_modified_date",
    value: "updated",
  },
  {
    id: 2,
    key: "created_date",
    value: "newest",
  },
  {
    id: 3,
    key: "bid_due_date",
    value: "Bid due date",
  },
  {
    id: 4,
    key: "sf_size",
    value: "Square Feet",
  },
];

const Description = ({
  description,
  ai_description,
  ai_description_fetched_at,
  sf_size,
  project_type,
  units,
  plan_drawings,
  address,
  city,
  state_short,
  stage_name,
  new_stage_name,
  hasFeatureAccess,
}) => {
  const [showRequestPlanModal, setShowRequestPlanModal] = useState(false);
  const [selectedSortItem, setSelectedSortItem] = useState();

  const handleSelectedSortItem = (itemId) => {
    const item = sortItems.find((item) => item.id === itemId);
    setSelectedSortItem(item.value);
  };

  const formatDescription = (text) => {
    const urlRegex =
      /(\b(https?|ftp|file):\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/gi;
    return text?.replace(urlRegex, function (url) {
      return '<a target="_blank" href="' + url + '">' + url + "</a>";
    });
  };

  const escapeHtml = (text) =>
    text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");

  const formatAiDescription = (text) => {
    if (!text) return "";

    const looksLikeHtml = /<\/?(p|h[1-6]|ul|ol|li|strong|em|a|br|div)\b/i.test(text);
    if (looksLikeHtml) {
      return text.replace(/^```(?:html|markdown|md|text)?\s*|\s*```$/gi, "").trim();
    }

    let html = escapeHtml(text);
    html = html.replace(/^```[\w]*\n?|\n?```$/gm, "");

    html = html.replace(/^#{3}\s+(.+)$/gm, '<h6 class="fw-bold mt-3 mb-2">$1</h6>');
    html = html.replace(/^#{2}\s+(.+)$/gm, '<h5 class="fw-bold mt-3 mb-2">$1</h5>');
    html = html.replace(/^#{1}\s+(.+)$/gm, '<h4 class="fw-bold mt-3 mb-2">$1</h4>');

    html = html.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
    html = html.replace(/__(.+?)__/g, "<strong>$1</strong>");
    html = html.replace(/(^|[^*\n])\*([^*\n]+)\*(?!\*)/g, "$1<em>$2</em>");

    html = html.replace(
      /\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g,
      '<a target="_blank" rel="noopener noreferrer" href="$2">$1</a>'
    );

    html = html.replace(/^(?:[-*•]\s+)(.+)$/gm, "<li>$1</li>");
    html = html.replace(/((?:<li>.*?<\/li>\n?)+)/g, '<ul class="mb-2 ps-4">$1</ul>');

    html = html.replace(
      /https?:\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|]/gi,
      (url, offset, full) => {
        const before = full.slice(Math.max(0, offset - 8), offset);
        if (before.includes('href="')) return url;
        return `<a target="_blank" rel="noopener noreferrer" href="${url}">${url}</a>`;
      }
    );

    html = html.replace(/\n{2,}/g, "<br/><br/>");
    html = html.replace(/\n/g, "<br/>");
    html = html.replace(/(<\/h[4-6]>|<ul class="mb-2 ps-4">)<br\/?>/g, "$1");
    html = html.replace(/<br\/?>(<\/ul>)/g, "$1");

    return html;
  };

  const openPlan = (planLink) => {
    window.open(planLink, "_blank");
  };

  return (
    <div>
      <div className="border description-body p-5 rounded-5 text-start mb-4">
        <div
          style={{ whiteSpace: "pre-line" }}
          dangerouslySetInnerHTML={{ __html: formatDescription(description) }}
        />
      </div>

      {ai_description ? (
        <div className="border description-body p-5 rounded-5 text-start mb-5">
          <div className="d-flex align-items-center mb-3">
            <FaRobot className="me-2 text-primary" size={16} />
            <h6 className="fw-bold mb-0">AI Description</h6>
            {ai_description_fetched_at ? (
              <span className="text-muted ms-2 text-12">
                {new Date(ai_description_fetched_at).toLocaleString()}
              </span>
            ) : null}
          </div>
          <div
            className="ai-description-content text-start"
            dangerouslySetInnerHTML={{
              __html: formatAiDescription(ai_description),
            }}
          />
        </div>
      ) : null}

      <div className="d-flex row mt-5">
        <div className="col py-5 gray-700-bg rounded-5 d-flex flex-column justify-content-center align-items-center">
          <LocationIcon width="20" height="20" fill="#AAAAAA" className="" />
          <div>
            <h6 className="fw-bold mt-2 text-13">Address</h6>
            <h6 className="description-value">
              <span style={!hasFeatureAccess ? { filter: "blur(5px)", pointerEvents: "none", userSelect: "none" } : {}}>{address !== null ? `${address},` : ""}</span>{" "}{city},{state_short}
            </h6>
          </div>
        </div>
        <div className="row row-cols-2 row-cols-md-3 mt-4 text-13 col-md-10 gy-2">
          <div className="col d-md-flex text-start">
            <BuildingIcon
              width="24"
              height="24"
              fill="#AAAAAA"
              className="me-md-3"
            />
            <div>
              <h6 className="fw-bold mt-2">Size</h6>
              <h6 className="description-value">
                {sf_size && sf_size >= 0 ? `${sf_size?.toLocaleString()} SQFT` : "- SQFT"}
              </h6>
            </div>
          </div>
          <div className="col d-md-flex text-start">
            <PlanIcon
              width="18"
              height="18"
              fill="#AAAAAA"
              className="me-md-3"
            />
            <div>
              <h6 className="fw-bold mt-2">Numbers of Units</h6>
              <h6 className="description-value">{units != "null" ? units : "-"}</h6>
            </div>
          </div>
          <div className="col d-md-flex text-start">
            <SizeIcon
              width="20"
              height="20"
              fill="#AAAAAA"
              className="me-md-3"
            />
            <div>
              <h6 className="fw-bold mt-2">Building Type</h6>
              <h6 className="description-value">{project_type}</h6>
            </div>
          </div>

          <div className="col d-md-flex text-start">
            <PenIcon
              width="19"
              height="19"
              fill="#AAAAAA"
              className="me-md-3"
            />
            <div>
              <h6 className="fw-bold mt-2">Plans/Drawings</h6>
              {plan_drawings === "None" || !plan_drawings ? (
                <h6 className="fw-light text-primary">Plan unavailable</h6>
              ) : (
                <h6
                  className="fw-light text-primary cursor-pointer"
                  onClick={() => openPlan(plan_drawings)}
                >
                  Download Plan
                </h6>
              )}
            </div>
          </div>
          <Modal
            title="Request Plans"
            show={showRequestPlanModal}
            onCloseModal={() => setShowRequestPlanModal(false)}
          >
            <RequestPlanForm
              selectedSortItem={selectedSortItem}
              sortItems={sortItems}
              handleSelectedSortItem={handleSelectedSortItem}
              setShowLogCallModal={setShowRequestPlanModal}
            />
          </Modal>
          <div className="col d-md-flex text-start">
            <PenIcon
              width="19"
              height="19"
              fill="#AAAAAA"
              className="me-md-3"
            />
            <div>
              <h6 className="fw-bold mt-2">Stage</h6>
              <h6 className="fw-light text-primary">{new_stage_name}</h6>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Description;
